import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import DialogComponent from 'components/Dialog';
import {DialogTypes} from 'common/names';
import i18n from '../../../../../i18n';
import Panel from '../../../../../components/Panel';

import {IDiplomatAdminUser} from 'types/DiplomatAdminUser';
import {useDispatch, useSelector} from 'react-redux';
import {layoutActions, layoutSelectors} from '../../../redux';
import Button from '../../../../../components/Button';
import {Trans} from 'react-i18next';
import ConfirmationWrapper from '../../../../../components/buttons/ConfirmationWrapper';
import {AuthenticationPolicies, IAuthenticationPolicies} from 'types/AuthenticationPolicies';
import FormikCheckbox from '../../../../../components/inputs/FormikCheckbox';
import LastUpdatedLabel from '../../../../../components/LastUpdatedLabel';
import {IPasswordData, PasswordData} from 'types/PasswordData';
import FormikDropdown from '../../../../../components/inputs/FormikDropdown';
import {enums} from 'types/enums';
import FormikNumberField from '../../../../../components/inputs/FormikNumberField';
import AddEditUserDialog from './AddEditUserDialog';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {GridColDef, DataGridPro, useGridApiRef} from '@mui/x-data-grid-pro';
import {Chip, Grid} from '@mui/material';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {DIPLOMAT, LDAP} from 'common/constants';
import {settingsActions, settingsSelectors} from 'redux/settings';
import {IAdminRole} from 'types/diplomat/settings/adminroles/AdminRole';
import {GridCentered} from 'components/GridCentered';
import {isEmpty} from 'lodash';
import {IPAccessRuleButton} from 'components/buttons/IPAccessRuleButton';
import {ReactFChildrenProps} from 'types/react/ReactFChildrenProps';

const UserAccounts = () => {
   const [selected, setSelected] = useState<IDiplomatAdminUser | null>();
   const [isAddOpened, addOpen] = useState(false);
   const [isEditOpened, editOpen] = useState(false);

   const dispatch = useDispatch();
   const admins = useSelector(layoutSelectors.admins);
   const tableRef = useGridApiRef();

   const rolesRef = useRef<IAdminRole[]>();
   rolesRef.current = useSelector(settingsSelectors.adminRoles);
   React.useEffect(() => {
      dispatch(settingsActions.fetchAdminRoles);
   }, [dispatch]);

   const cols: GridColDef[] = [
      {field: 'contactName', headerName: i18n.t('Contact Name'), flex: 1, resizable: true},
      {
         field: 'adminRoleIds',
         headerName: i18n.t('Roles'),
         flex: 1,
         resizable: true,
         renderCell(params) {
            return (params.value as string[])
               ?.map((roleId) => rolesRef.current?.find((r) => r.id === roleId)?.name)
               .sort()
               .map((role) => <Chip key={role} label={role} />);
         },
      },
      {field: 'userName', headerName: i18n.t('Username'), flex: 1},
      {
         field: 'ldapPasswordAuthentication',
         headerName: i18n.t('Type'),
         flex: 1,
         resizable: true,
         valueFormatter: (params) => (params.value ? LDAP : DIPLOMAT),
      },
      // {field: 'ruserIdole', headerName: i18n.t('User ID'), flex: 1},
      // {
      //    field: 'twoFactorAuthentication',
      //    headerName: i18n.t('Two-Factor\nAuthentication\nRequired'),
      //    flex: 1,
      // },
   ];

   useEffect(() => {
      dispatch(layoutActions.loadUserAdmins());
   }, [dispatch]);

   const add = useCallback(
      (admin: IDiplomatAdminUser, formikBag: FormOnSubmitHelpers<IDiplomatAdminUser>) =>
         dispatch(
            layoutActions.addUserAdmin(admin, {
               onSuccess: () => {
                  addOpen(false);
                  dispatch(layoutActions.loadUserAdmins());
               },
               onError: (errors: any) => formikBag.setErrors(errors),
            })
         ),
      [dispatch, addOpen]
   );
   const edit = useCallback(
      (admin: IDiplomatAdminUser, formikBag: FormOnSubmitHelpers<IDiplomatAdminUser>) =>
         dispatch(
            layoutActions.editUserAdmin(admin, {
               onSuccess: () => {
                  editOpen(false);
                  dispatch(layoutActions.loadUserAdmins());
                  setSelected(null);
                  tableRef.current.setSelectionModel([]);
               },
               onError: (errors: any) => formikBag.setErrors(errors),
            })
         ),
      [dispatch, editOpen, setSelected, tableRef]
   );
   const deleteAdmin = useCallback(
      (id: string) => {
         dispatch(
            layoutActions.deleteUserAdmin(id, {
               onSuccess: () => dispatch(layoutActions.loadUserAdmins()),
            })
         );
      },
      [dispatch]
   );

   return (
      <Panel name={i18n.t('User Accounts')}>
         <div style={{height: 350, width: '100%'}}>
            <DataGridPro
               apiRef={tableRef}
               rows={admins}
               columns={cols}
               pageSize={4}
               onSelectionModelChange={(selectionModel) =>
                  setSelected(admins.find((a) => a.id === selectionModel[0]) as IDiplomatAdminUser)
               }
            />
         </div>
         <Grid container spacing={3} justifyContent="center">
            <Grid item>
               <PrimaryButton onClick={() => addOpen(true)}>
                  <Trans>Add</Trans>
               </PrimaryButton>
            </Grid>
            <Grid item>
               <PrimaryButton onClick={() => editOpen(true)} disabled={!selected}>
                  <Trans>Edit</Trans>
               </PrimaryButton>
            </Grid>
            <Grid item>
               <ConfirmationWrapper
                  onOk={() => selected && deleteAdmin(selected.id)}
                  disabled={!selected}
               >
                  <Button color="secondary" disabled={!selected}>
                     <Trans>Delete</Trans>
                  </Button>
               </ConfirmationWrapper>
            </Grid>
         </Grid>

         {isAddOpened ? <AddEditUserDialog onSubmit={add} onClose={() => addOpen(false)} /> : null}
         {isEditOpened && selected ? (
            <AddEditUserDialog user={selected} onSubmit={edit} onClose={() => editOpen(false)} />
         ) : null}
      </Panel>
   );
};

type AuthPoliciesProps = {};

const AuthPolicies: ReactFChildrenProps<AuthPoliciesProps> = (props) => {
   const dispatch = useDispatch();
   const authPolicies = useSelector(layoutSelectors.authPolicies);

   useEffect(() => {
      dispatch(layoutActions.getAuthPolicies);
   }, [dispatch]);

   const submit = useCallback(
      (data: IAuthenticationPolicies, formikBag: FormOnSubmitHelpers<IAuthenticationPolicies>) => {
         dispatch(
            layoutActions.postAuthPolicies(data, {
               onSuccess: () => {
                  dispatch(layoutActions.getAuthPolicies);
                  formikBag.setSubmitting(false);
               },
               onError: (errors: any) => formikBag.setErrors(errors),
            })
         );
      },
      [dispatch]
   );

   return (
      <Panel name={i18n.t('Authentication Policies')}>
         {!isEmpty(authPolicies) && (
            <Formik
               initialValues={AuthenticationPolicies.cast(authPolicies) as IAuthenticationPolicies}
               validationSchema={AuthenticationPolicies}
               onSubmit={submit}
               // enableReinitialize
            >
               <>
                  <Grid container spacing={3}>
                     <Grid item xs={4}>
                        <FormikCheckbox
                           autoFocus
                           name="caseInsensitiveAuthentication"
                           label={i18n.t('Ignore case sensitivity for usernames')}
                        />
                     </Grid>
                     <GridCentered item>
                        <IPAccessRuleButton
                           isAdmin
                           text={i18n.t(`IP Access Restrictions allow you to control which IP addresses are allowed to connect to
       the Diplomat MFT Admin Portal. Choose to allow or deny certain IPs.`)}
                        />
                     </GridCentered>
                  </Grid>
                  <Grid container spacing={3}>
                     <Grid item xs={4}>
                        <FormikCheckbox
                           autoFocus
                           name="ssoOnly"
                           label={i18n.t('Require SSO for Administrator Login')}
                        />
                     </Grid>
                  </Grid>
                  <Grid container spacing={3} justifyContent="center">
                     <Grid item>
                        <FormFooterButtons submitLabel={i18n.t('Save')} resettable />
                     </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                     <Grid item>
                        <LastUpdatedLabel
                           date={authPolicies.timestamp}
                           author={authPolicies.lastUser}
                        />
                     </Grid>
                  </Grid>
               </>
            </Formik>
         )}
      </Panel>
   );
};

type PasswordPoliciesProps = {};

const PasswordPolicies: ReactFChildrenProps<PasswordPoliciesProps> = (props) => {
   const dispatch = useDispatch();
   const passwordData = useSelector(layoutSelectors.passwordData);

   useEffect(() => {
      dispatch(layoutActions.getPasswordData);
   }, [dispatch]);

   const submit = useCallback(
      (passwordData: IPasswordData, formikBag: FormOnSubmitHelpers<IPasswordData>) => {
         dispatch(
            layoutActions.postPasswordData(passwordData, {
               onSuccess: () => {
                  formikBag.setSubmitting(false);
                  dispatch(layoutActions.getPasswordData);
               },
               onError: (errors: any) => formikBag.setErrors(errors),
            })
         );
      },
      [dispatch]
   );

   return (
      <Panel name={i18n.t('Password Policies')}>
         <Formik
            initialValues={PasswordData.cast(passwordData) as IPasswordData}
            validationSchema={PasswordData}
            enableReinitialize
            onSubmit={submit}
         >
            <>
               <Grid container spacing={3}>
                  <Grid item sm={12} lg={6}>
                     <FormikDropdown
                        name="passwordUpdateInterval"
                        placeholder={i18n.t('Minimum password change frequency')}
                        options={enums.passwordUpdateInterval.options}
                     />
                  </Grid>
               </Grid>
               <Panel name={i18n.t('Password Format')}>
                  <Grid container spacing={3}>
                     <Grid item sm={12} lg={5}>
                        <FormikNumberField
                           name="minNumChars"
                           placeholder={i18n.t('Minimum number of characters')}
                        />
                     </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                     <Grid item>
                        <FormikCheckbox
                           name="mixedCaseRequired"
                           label={i18n.t(
                              'Passwords must contain both uppercase and lowercase letters'
                           )}
                        />
                     </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                     <Grid item>
                        <FormikCheckbox
                           name="alphaNumericRequired"
                           label={i18n.t(
                              'Passwords must contain both alpha and numeric characters'
                           )}
                        />
                     </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                     <Grid item>
                        {' '}
                        <FormikCheckbox
                           name="specialCharsRequired"
                           label={
                              <>
                                 <Trans>
                                    Passwords must contain at least 1 special character. Allowable
                                    special characters are
                                 </Trans>
                                 : <br />
                                 {
                                    '!  "  #  $  %  &  \'  (  )  *  +  ,  -  .  /  :  ;  <  =  >  ?  @  [  \\  ]  ^  _  `  {  |  }  ~'
                                 }
                              </>
                           }
                        />
                     </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                     <Grid item>
                        <b>NOTE: </b>&nbsp; Format changes do not affect existing passwords and will
                        be used to validate new passwords only.
                     </Grid>
                  </Grid>
               </Panel>
               <Grid container spacing={3}>
                  <Grid item>
                     <LastUpdatedLabel
                        date={passwordData.timestamp}
                        author={passwordData.lastUser}
                     />
                  </Grid>
               </Grid>
               <Grid container spacing={3} justifyContent="center">
                  <Grid item>
                     <FormFooterButtons submitLabel={i18n.t('Save')} resettable />
                  </Grid>
               </Grid>
               {/* {JSON.stringify(formikProps.values)} */}
            </>
         </Formik>
      </Panel>
   );
};

type Props = {
   onClose: () => void;
};

const UserAccountSettingDialog: ReactFChildrenProps<Props> = memo((props) => {
   const {onClose} = props;

   return (
      <DialogComponent
         maxWidth="lg"
         header={DialogTypes.UserAccountSettingDialog.dialogName}
         close={onClose}
      >
         <UserAccounts />
         <AuthPolicies />
         <PasswordPolicies />
         <Grid container spacing={3} justifyContent="center">
            <Grid item>
               <Button type="button" onClick={onClose}>
                  <Trans>Close</Trans>
               </Button>
            </Grid>
         </Grid>
      </DialogComponent>
   );
});

export default UserAccountSettingDialog;
